import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { legalService, PendingLegalDocument } from './legalService';
import { LegalConsentOverlay } from './LegalConsentOverlay';

interface LegalConsentGateProps {
  children: React.ReactNode;
}

/**
 * Guards the authenticated area: after login it checks whether the current
 * organization still needs to accept updated legal documents (AGB / AVV).
 *
 * If documents are pending, a blocking overlay is shown. Accepting records the
 * consent (visible in Django admin) and lets the user continue. Declining logs
 * the user out and returns them to the login screen (where the check — and
 * thus the overlay — will trigger again on the next login).
 */
export const LegalConsentGate: React.FC<LegalConsentGateProps> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingLegalDocument[]>([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setPending([]);
      setChecked(false);
      return;
    }

    const check = async () => {
      const docs = await legalService.getPendingDocuments();
      if (!cancelled) {
        setPending(docs);
        setChecked(true);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleAccept = async () => {
    await legalService.acceptDocuments();
    setPending([]);
  };

  const handleDecline = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const showOverlay = isAuthenticated && checked && pending.length > 0;

  return (
    <>
      {children}
      {showOverlay && (
        <LegalConsentOverlay
          documents={pending}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )}
    </>
  );
};
